import { createContext, useContext } from 'react';

import type { useLoggerScreen } from './useLoggerScreen';
import type { LoggerCursor } from './useLoggerCursor';
import type { useRestAdvance } from './useRestAdvance';

// Shared logger context bag. Carries the full useLoggerScreen return so the
// logger subtree (the timeline screen) can read it without threading ~35 props
// down from LoggerScreen. A single context (not a stable/volatile split) is
// deliberate: no card is React.memo'd and re-render optimisation is out of
// scope, so a split would be behaviour-identical while adding surface.
//
// Provider lives in LoggerScreen. Extracted out of the now-removed
// LoggerEntryList.tsx (card logger) so the context survives that deletion —
// the timeline logger reads the same bag. The legacy `EntryList` name is kept
// to avoid churn across the timeline importers; it no longer implies a card.
// The bag = useLoggerScreen's return PLUS the focus cursor (useLoggerCursor),
// which is owned in the LoggerScreen body (above the Modal) so it survives
// minimize -> resume (LG-13). Both are merged into the provider value there.
type LoggerContextValue = ReturnType<typeof useLoggerScreen> &
  LoggerCursor & {
    /** The post-log focus transition. Lives above the Modal beside the cursor
     *  (LG-13): it holds a decision taken but not yet applied, and a minimize
     *  mid-rest would otherwise destroy it while the timer kept counting. */
    restAdvance: ReturnType<typeof useRestAdvance>;
  };

export const LoggerContext = createContext<LoggerContextValue | null>(
  null,
);

export function useLoggerContext(): LoggerContextValue {
  const ctx = useContext(LoggerContext);
  if (ctx === null) {
    throw new Error(
      'Logger subtree must be rendered inside LoggerContext.Provider',
    );
  }
  return ctx;
}
